import type { CheckHourlyEarningType, TimesheetEarningType } from "../integrations/check/check-earning-type";

import { TIME_TYPE_LABELS, TimeTypeValues } from "./time-type";
import type { UnionRateFringeCategory } from "./union-rate-fringe";

/** Daily-report grid grouping key for rows with no activity; a UI placeholder that must never be persisted as an activity id. */
export const NO_ACTIVITY_SENTINEL = "no-activity";

export const ActivityPayRateTypeValues = ["default", "custom", "pw", "prg"] as const;
export type ActivityPayRateType = (typeof ActivityPayRateTypeValues)[number];

export const ActivityScopeValues = [
  "timesheets",
  "expenses",
  "reimbursements",
  "quantities",
  "materials",
] as const;
export type ActivityScope = (typeof ActivityScopeValues)[number];

export const DefaultActivityScopeValues: ActivityScope[] = [
  "timesheets",
  "expenses",
  "reimbursements",
  "quantities",
  "materials",
];

export const BenefitContributionCalculationMethodValues = ["percent", "per_hour"] as const;
export type BenefitContributionCalculationMethod =
  (typeof BenefitContributionCalculationMethodValues)[number];

export const ActivityQualificationLevelValues = ["journeyman", "apprentice"] as const;
export type ActivityQualificationLevel = (typeof ActivityQualificationLevelValues)[number];

export type ActivityQBObjectType = "class" | "service_item";

/** Time-off kinds an activity can default its timesheets to. Applied when the timesheet is saved. */
export const ActivityTimeOffEarningCodeKeyValues = [
  "pto",
  "sick",
  "paid_holiday",
] as const satisfies readonly TimesheetEarningType[];
export type ActivityTimeOffEarningCodeKey = (typeof ActivityTimeOffEarningCodeKeyValues)[number];

/** Keys of an activity's earning code map: worked time types (labeled at payroll) plus time-off kinds. */
export const ActivityEarningCodeKeyValues = [
  ...TimeTypeValues,
  ...ActivityTimeOffEarningCodeKeyValues,
] as const;
export type ActivityEarningCodeKey = (typeof ActivityEarningCodeKeyValues)[number];

export const ACTIVITY_EARNING_CODE_KEY_LABELS = {
  ...TIME_TYPE_LABELS,
  pto: "Vacation",
  sick: "Sick",
  paid_holiday: "Holiday",
} satisfies Record<ActivityEarningCodeKey, string>;

// A custom earning code attached to hours must share the Check earning type those hours are paid under;
// recodes enforce the same invariant, so this is the allowlist for activity earning code mappings.
export const ACTIVITY_EARNING_CODE_KEY_TO_CHECK_EARNING_TYPES = {
  reg: ["hourly", "regular"],
  ot: ["overtime"],
  dot: ["double_overtime"],
  pto: ["pto"],
  sick: ["sick"],
  paid_holiday: ["paid_holiday"],
} as const satisfies Record<ActivityEarningCodeKey, readonly CheckHourlyEarningType[]>;

export type ActivityBenefitContribution = {
  _id: string;
  label: string;
  category: UnionRateFringeCategory;
  /** Amount per hour */
  amount?: number;
  dynamic_offset?: boolean;
  /** Should not count toward fringe offsets */
  non_bonafide?: boolean;
  integrations?: {
    contractors_plan?: {
      is_cp_benefit: boolean;
    };
  };
};
